import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const CustomerAuthContext = createContext(null);

import { BACKEND_URL, API } from '../utils/apiConfig';

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('customer_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentCustomer();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentCustomer = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`);
      setCustomer(response.data);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (mobile) => {
    try {
      const response = await axios.post(`${API}/user/login`, { mobile });
      const { access_token, customer: customerData, is_new_user, is_profile_complete } = response.data;
      
      setToken(access_token);
      // Include is_profile_complete in customer state
      setCustomer({ ...customerData, is_profile_complete });
      localStorage.setItem('customer_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { 
        success: true, 
        isNewUser: is_new_user, 
        isProfileComplete: is_profile_complete,
        customer: customerData
      };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const authToken = localStorage.getItem('customer_token');
      const response = await axios.put(`${API}/user/profile`, profileData, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCustomer(response.data.profile);
      return { success: true, profile: response.data.profile };
    } catch (error) {
      // Handle Pydantic validation errors (array) or string errors
      let errorMessage = 'Failed to update profile';
      const detail = error.response?.data?.detail;
      if (detail) {
        if (Array.isArray(detail)) {
          // Pydantic validation error - extract message from first error
          errorMessage = detail[0]?.msg || detail[0]?.message || 'Validation error';
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || 'Validation error';
        }
      }
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = () => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem('customer_token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const refreshProfile = async () => {
    if (token) {
      await fetchCurrentCustomer();
    }
  };

  return (
    <CustomerAuthContext.Provider value={{ 
      customer, 
      token, 
      login, 
      updateProfile, 
      logout, 
      loading,
      refreshProfile,
      isAuthenticated: !!token && !!customer,
      isProfileComplete: customer?.is_profile_complete || false
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};
